// SC-1323 §5.2: the Portal Player ⇄ learning portal postMessage protocol, version 1. The website
// mirrors it in resources/js/portal/support/portalMessages.js; both test against the byte-identical
// tests/fixtures/contracts/portal-messages-v1.json. Change both together, never one.
export const PORTAL_NS = "springcast-portal"
export const PORTAL_VERSION = 1
export const PORTAL_TO_PLAYER = ["init", "seek", "play", "pause", "mode"]
export const PLAYER_TO_PORTAL = ["ready", "time", "event", "error"]
export const EVENT_ACTIONS = ["play", "pause", "resume", "seek", "ended", "mode", "mute", "unmute", "fullscreen_enter", "fullscreen_exit", "quality_switch", "buffering_start", "buffering_end", "cta"]
export const ERROR_CODES = ["media_failed", "traffic_blocked", "unavailable"]

const MODES = ["video", "audio"]
const ID = /^[A-Za-z0-9]{1,64}$/
const isSeconds = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0
const isDuration = (value) => value === null || isSeconds(value)
const isMode = (value) => MODES.includes(value)
const isId = (value) => typeof value === "string" && ID.test(value)
const isCount = (value) => Number.isInteger(value) && value >= 0
const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value)

function validVideoFields(data) {
    if (data.viewport_pixels !== undefined && !(isObject(data.viewport_pixels) && isCount(data.viewport_pixels.width) && isCount(data.viewport_pixels.height))) {
        return false
    }

    // Like the podcast beacons: any video event may carry the current rendition's height.
    if (data.quality_height !== undefined && !isCount(data.quality_height)) {
        return false
    }

    return data.fullscreen === undefined || typeof data.fullscreen === "boolean"
}

const EVENT_FIELDS = {
    seek: (data) => isSeconds(data.from),
    mode: (data) => isId(data.from_item) && isId(data.to_item) && isMode(data.to_mode),
    cta: (data) => typeof data.cta_id === "string" && data.cta_id.length >= 1 && data.cta_id.length <= 64,
    buffering_end: (data) => data.buffering_ms === undefined || isCount(data.buffering_ms),
}

const PORTAL_TO_PLAYER_PAYLOADS = {
    init: (data) =>
        isMode(data.mode) &&
        isObject(data.resume) &&
        Object.entries(data.resume).every(([mode, seconds]) => isMode(mode) && isSeconds(seconds)) &&
        (data.autoplay === undefined || typeof data.autoplay === "boolean"),
    seek: (data) => isSeconds(data.position),
    play: () => true,
    pause: () => true,
    mode: (data) => isMode(data.mode),
}

const PLAYER_TO_PORTAL_PAYLOADS = {
    ready: (data) =>
        isDuration(data.duration) &&
        Array.isArray(data.modes) &&
        data.modes.length > 0 &&
        data.modes.every(isMode) &&
        data.modes.includes(data.mode) &&
        isObject(data.items) &&
        Object.entries(data.items).every(([mode, id]) => isMode(mode) && isId(id)),
    time: (data) => isId(data.item_id) && isSeconds(data.position) && isDuration(data.duration) && isMode(data.mode) && typeof data.playing === "boolean",
    event: (data) =>
        isId(data.item_id) &&
        EVENT_ACTIONS.includes(data.action) &&
        isSeconds(data.position) &&
        isDuration(data.duration) &&
        isMode(data.mode) &&
        validVideoFields(data) &&
        (EVENT_FIELDS[data.action]?.(data) ?? true),
    error: (data) => ERROR_CODES.includes(data.code),
}

export function portalMessage(type, fields = {}) {
    return { ...fields, ns: PORTAL_NS, v: PORTAL_VERSION, type }
}

export function isPortalEnvelope(data, accept) {
    return data !== null && typeof data === "object" && data.ns === PORTAL_NS && data.v === PORTAL_VERSION && accept.includes(data.type)
}

export function isPortalMessage(event, { origin, source, accept }) {
    return typeof origin === "string" && origin !== "" && !!source && event?.origin === origin && event.source === source && isPortalEnvelope(event.data, accept)
}

export function isValidPortalToPlayer(data) {
    return isPortalEnvelope(data, PORTAL_TO_PLAYER) && PORTAL_TO_PLAYER_PAYLOADS[data.type](data)
}

export function isValidPlayerToPortal(data) {
    return isPortalEnvelope(data, PLAYER_TO_PORTAL) && PLAYER_TO_PORTAL_PAYLOADS[data.type](data)
}

export function createPlayerBridge({ win, parent, origin, onMessage }) {
    const listener = (event) => {
        if (!isPortalMessage(event, { origin, source: parent, accept: PORTAL_TO_PLAYER }) || !isValidPortalToPlayer(event.data)) {
            return
        }

        try {
            onMessage(event.data)
        } catch {
            console.warn("[portal-bridge] message handler failed")
        }
    }

    win.addEventListener("message", listener)

    return {
        post(type, fields = {}) {
            if (!origin || !parent || parent === win || !PLAYER_TO_PORTAL.includes(type)) {
                return
            }

            const message = portalMessage(type, fields)

            if (!isValidPlayerToPortal(message)) {
                console.warn(`[portal-bridge] refusing an invalid ${type} message`)

                return
            }

            try {
                parent.postMessage(message, origin)
            } catch {
                console.warn("[portal-bridge] postMessage failed")
            }
        },
        destroy() {
            win.removeEventListener("message", listener)
        },
    }
}
